// Registro movimenti magazzino con creazione manuale (sorgente/destinazione)
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, ArrowRightLeft, ArrowLeftRight, Warehouse, User } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Table } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface Material { id: string; code: string; name: string }

interface Technician {
  id: string
  name?: string
  firstName?: string
  lastName?: string
  email?: string
}

interface Movement {
  id: string
  movementType: string
  operationType?: string
  materialId: string
  material?: { id: string; code: string; name: string }
  quantity: number
  notes?: string
  unitCost?: number
  fromTechnicianId?: string | null
  toTechnicianId?: string | null
  fromTechnicianName?: string | null
  toTechnicianName?: string | null
  createdAt: string
}

// Operazione → label visuale
const OPERATION_OPTIONS = [
  { value: 'inbound_external',     label: '↑ Carico da fornitore → Magazzino' },
  { value: 'outbound_technician',  label: '→ Magazzino → Tecnico' },
  { value: 'return_technician',    label: '← Tecnico → Magazzino' },
  { value: 'transfer_technicians', label: '⇄ Tecnico → Tecnico' },
  { value: 'use_warehouse',        label: '↓ Consumo da Magazzino' },
  { value: 'use_technician',       label: '↓ Consumo da Tecnico' },
  { value: 'inventory_warehouse',  label: '📋 Inventario Magazzino Centrale' },
  { value: 'inventory_technician', label: '📋 Inventario Tecnico (van)' },
]

// Operazione → badge
const OP_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'default'> = {
  inbound_external: 'success',
  outbound_technician: 'default',
  return_technician: 'warning',
  transfer_technicians: 'default',
  use_warehouse: 'danger',
  use_technician: 'danger',
  inventory_warehouse: 'default',
  inventory_technician: 'default',
}

// Operazione → label badge
const OP_LABEL: Record<string, string> = {
  inbound_external: 'Carico',
  outbound_technician: 'Mag → Tec',
  return_technician: 'Reso',
  transfer_technicians: 'Trasf.',
  use_warehouse: 'Consumo',
  use_technician: 'Consumo',
  inventory_warehouse: 'Inventario',
  inventory_technician: 'Inventario',
}

// Deriva "Da" e "A" da un movimento
function deriveSrcDst(m: Movement): { src: string; dst: string } {
  const fromTech = m.fromTechnicianName ?? (m.fromTechnicianId ? '...' : null)
  const toTech = m.toTechnicianName ?? (m.toTechnicianId ? '...' : null)

  if (m.movementType === 'restock' && !fromTech && !toTech) return { src: 'Fornitore', dst: 'Magazzino' }
  if (m.movementType === 'transfer' && !fromTech && toTech) return { src: 'Magazzino', dst: toTech! }
  if (m.movementType === 'return' && fromTech && !toTech) return { src: fromTech!, dst: 'Magazzino' }
  if (m.movementType === 'transfer' && fromTech && toTech) return { src: fromTech!, dst: toTech! }
  if (m.movementType === 'use' && !fromTech) return { src: 'Magazzino', dst: 'Consumo' }
  if (m.movementType === 'use' && fromTech) return { src: fromTech!, dst: 'Consumo' }
  if (m.movementType === 'inventory' && !fromTech) return { src: '—', dst: 'Magazzino' }
  if (m.movementType === 'inventory' && fromTech) return { src: '—', dst: fromTech! }
  return { src: '—', dst: '—' }
}

const schema = z.object({
  operationType: z.enum([
    'inbound_external', 'outbound_technician', 'return_technician',
    'transfer_technicians', 'use_warehouse', 'use_technician',
    'inventory_warehouse', 'inventory_technician',
  ]),
  materialId: z.string().min(1, 'Seleziona un materiale'),
  quantity: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive('Quantità deve essere positiva'),
  ),
  fromTechnicianId: z.string().uuid().optional(),
  toTechnicianId: z.string().uuid().optional(),
  notes: z.string().optional(),
  unitCost: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
})

type FormData = z.infer<typeof schema>

function getTechName(t: Technician): string {
  if (t.name) return t.name
  if (t.firstName || t.lastName) return `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim()
  return t.email ?? t.id.slice(0, 8)
}

function WarehouseTransferForm({ onSuccess }: { onSuccess: () => void }) {
  const [fromId, setFromId] = useState('')
  const [toId, setToId]     = useState('')
  const [materialId, setMaterialId] = useState('')
  const [quantity, setQuantity]     = useState('')

  const { data: warehouses = [] } = useQuery<any[]>({
    queryKey: ['warehouses'],
    queryFn: () => api.get('/warehouse/locations').then(r => r.data),
  })

  const { data: materials = [] } = useQuery<any[]>({
    queryKey: ['warehouse-materials-all'],
    queryFn: () => api.get('/warehouse/materials').then(r => r.data),
  })

  const mutation = useMutation({
    mutationFn: () => api.post('/warehouse/movements', {
      operationType: 'warehouse_transfer',
      materialId,
      quantity: Number(quantity),
      fromWarehouseId: fromId,
      toWarehouseId: toId,
    }),
    onSuccess: () => {
      onSuccess()
      setFromId(''); setToId(''); setMaterialId(''); setQuantity('')
    },
  })

  return (
    <div className="bg-[color-mix(in_srgb,var(--fc-primary)_5%,transparent)] border border-[color-mix(in_srgb,var(--fc-primary)_20%,transparent)] rounded-lg p-4 space-y-3 mb-6">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4" />
        Trasferimento tra Magazzini
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[var(--fc-foreground-muted)] mb-1 block">Da magazzino</label>
          <select
            value={fromId}
            onChange={e => setFromId(e.target.value)}
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-background)] px-2 py-1.5 text-sm"
          >
            <option value="">— seleziona —</option>
            {warehouses.map((w: any) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--fc-foreground-muted)] mb-1 block">A magazzino</label>
          <select
            value={toId}
            onChange={e => setToId(e.target.value)}
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-background)] px-2 py-1.5 text-sm"
          >
            <option value="">— seleziona —</option>
            {warehouses.filter((w: any) => w.id !== fromId).map((w: any) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[var(--fc-foreground-muted)] mb-1 block">Materiale</label>
          <select
            value={materialId}
            onChange={e => setMaterialId(e.target.value)}
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-background)] px-2 py-1.5 text-sm"
          >
            <option value="">— seleziona —</option>
            {materials.map((m: any) => (
              <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--fc-foreground-muted)] mb-1 block">Quantità</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
            placeholder="0"
            className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-background)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          disabled={!fromId || !toId || !materialId || !quantity || mutation.isPending}
          onClick={() => mutation.mutate()}
          className="px-4 py-1.5 bg-[var(--fc-primary)] text-white rounded text-sm font-medium disabled:opacity-50 transition-opacity"
        >
          {mutation.isPending ? 'Trasferimento...' : 'Trasferisci'}
        </button>
        {mutation.isError && (
          <p className="text-xs text-[var(--fc-danger)]">
            Errore: {(mutation.error as any)?.response?.data?.message ?? 'Trasferimento fallito'}
          </p>
        )}
        {mutation.isSuccess && (
          <p className="text-xs text-[var(--fc-success)]">Trasferimento effettuato!</p>
        )}
      </div>
    </div>
  )
}

export function MovementsPage() {
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)

  const { data: movements = [], isLoading } = useQuery({
    queryKey: ['movements'],
    queryFn: async () => {
      try {
        const res = await api.get<Movement[]>('/warehouse/movements')
        return Array.isArray(res.data) ? res.data : []
      } catch {
        return [] as Movement[]
      }
    },
  })

  const { data: materials = [] } = useQuery({
    queryKey: ['materials', 'all'],
    queryFn: async () => {
      try {
        const res = await api.get<Material[]>('/warehouse/materials')
        return Array.isArray(res.data) ? res.data : []
      } catch {
        return [] as Material[]
      }
    },
  })

  const { data: technicians = [] } = useQuery({
    queryKey: ['technicians'],
    queryFn: async () => {
      try {
        const res = await api.get<Technician[]>('/technicians')
        return (res.data ?? []).filter((t) => Boolean(t.id))
      } catch {
        return [] as Technician[]
      }
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { operationType: 'inbound_external', quantity: undefined, notes: '' },
  })

  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      await api.post('/warehouse/movements', data)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['movements'] })
      void queryClient.invalidateQueries({ queryKey: ['materials'] })
      setFormOpen(false)
      reset()
    },
  })

  const operationType = watch('operationType')

  // Visibilità campi in base al tipo operazione
  const needsFromTech = ['return_technician', 'transfer_technicians', 'use_technician', 'inventory_technician'].includes(operationType)
  const needsToTech   = ['outbound_technician', 'transfer_technicians'].includes(operationType)
  const showUnitCost  = operationType === 'inbound_external'
  const isInventory   = operationType === 'inventory_warehouse' || operationType === 'inventory_technician'

  const techOptions = technicians.map((t) => ({ value: t.id, label: getTechName(t) }))
  const materialOptions = materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))

  const columns = [
    {
      key: 'op',
      header: 'Operazione',
      cell: (m: Movement) => {
        // Usa movementType per derivare l'operazione leggibile
        const { src, dst } = deriveSrcDst(m)
        const variant = OP_VARIANT[m.movementType] ?? 'default'
        const label = OP_LABEL[m.movementType] ?? m.movementType
        return (
          <Badge variant={variant}>
            {label}
          </Badge>
        )
      },
      className: 'w-28',
    },
    {
      key: 'src_dst',
      header: 'Da → A',
      cell: (m: Movement) => {
        const { src, dst } = deriveSrcDst(m)
        return (
          <span className="flex items-center gap-1 text-sm">
            <span className="text-[var(--fc-foreground-muted)]">{src}</span>
            <ArrowRightLeft className="h-3 w-3 text-[var(--fc-foreground-muted)]" />
            <span className="font-medium text-[var(--fc-foreground)]">{dst}</span>
          </span>
        )
      },
    },
    {
      key: 'material',
      header: 'Materiale',
      cell: (m: Movement) => (
        <span>
          {m.material
            ? <><span className="font-mono text-xs text-[var(--fc-foreground-muted)]">{m.material.code}</span>{' '}<span className="font-medium">{m.material.name}</span></>
            : <span className="font-mono text-xs">{m.materialId.slice(0, 8)}</span>
          }
        </span>
      ),
    },
    {
      key: 'quantity',
      header: 'Q.tà',
      cell: (m: Movement) => (
        <span className={`font-semibold ${m.movementType === 'use' ? 'text-[var(--fc-danger)]' : m.movementType === 'restock' ? 'text-[var(--fc-success)]' : ''}`}>
          {m.movementType === 'use' ? '-' : '+'}{Math.abs(m.quantity)}
        </span>
      ),
      className: 'w-20',
    },
    {
      key: 'date',
      header: 'Data',
      cell: (m: Movement) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {formatDate(m.createdAt, 'dd/MM/yyyy HH:mm')}
        </span>
      ),
      className: 'w-36',
    },
    {
      key: 'notes',
      header: 'Note',
      cell: (m: Movement) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">{m.notes ?? '—'}</span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Movimenti"
        breadcrumb={[{ label: 'Magazzino', href: '/warehouse' }, { label: 'Movimenti' }]}
        actions={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => { reset({ operationType: 'inbound_external' }); setFormOpen(true) }}
          >
            Nuovo movimento
          </Button>
        }
      />

      <WarehouseTransferForm onSuccess={() => queryClient.invalidateQueries({ queryKey: ['movements'] })} />

      <Table
        data={movements}
        columns={columns}
        keyExtractor={(m) => m.id}
        isLoading={isLoading}
        emptyMessage="Nessun movimento registrato"
      />

      {/* Modal nuovo movimento */}
      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); reset() }}
        title="Nuovo movimento magazzino"
        size="md"
      >
        <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4">
          {/* Tipo operazione */}
          <Select
            label="Tipo operazione *"
            options={OPERATION_OPTIONS}
            value={operationType}
            onValueChange={(v) => {
              setValue('operationType', v as FormData['operationType'])
              setValue('fromTechnicianId', undefined)
              setValue('toTechnicianId', undefined)
            }}
          />

          {/* Tecnico sorgente */}
          {needsFromTech && (
            <Select
              label={operationType === 'inventory_technician' ? 'Tecnico (van da inventariare) *' : 'Tecnico sorgente *'}
              options={techOptions}
              value={watch('fromTechnicianId') || undefined}
              onValueChange={(v) => setValue('fromTechnicianId', v)}
              placeholder="Seleziona tecnico..."
              error={errors.fromTechnicianId?.message}
            />
          )}

          {/* Tecnico destinazione */}
          {needsToTech && (
            <Select
              label="Tecnico destinazione *"
              options={techOptions}
              value={watch('toTechnicianId') || undefined}
              onValueChange={(v) => setValue('toTechnicianId', v)}
              placeholder="Seleziona tecnico..."
              error={errors.toTechnicianId?.message}
            />
          )}

          {/* Materiale */}
          <Select
            label="Materiale *"
            options={materialOptions}
            value={watch('materialId') ?? ''}
            onValueChange={(v) => setValue('materialId', v)}
            error={errors.materialId?.message}
            placeholder="Seleziona materiale..."
          />

          {/* Quantità + costo */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={isInventory ? 'Quantità inventariata *' : 'Quantità *'}
              type="number"
              step="1"
              min="0"
              {...register('quantity')}
              error={errors.quantity?.message}
              placeholder="0"
            />
            {showUnitCost && (
              <Input
                label="Costo unitario (€)"
                type="number"
                step="0.01"
                {...register('unitCost')}
                error={errors.unitCost?.message}
                placeholder="12.50"
              />
            )}
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Note</label>
            <textarea
              {...register('notes')}
              rows={2}
              className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
              placeholder="Motivo del movimento, riferimento ordine..."
            />
          </div>

          {createMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio. Riprova.</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => { setFormOpen(false); reset() }}>
              Annulla
            </Button>
            <Button type="submit" disabled={isSubmitting || createMutation.isPending}>
              {createMutation.isPending ? 'Salvataggio...' : 'Registra movimento'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
